import { T } from '../theme';
import { Eyebrow, H1, Stage, Sub } from '../primitives';
import type { DisclosuresVM } from '../../formatters/deckContent';

// Required compliance copy — verbatim from the approved disclosure text. Any
// wording change here needs compliance sign-off, not just a copy edit.
const IMPORTANT_NOTICE =
  'This report is a hypothetical illustration based on information provided by you the client with respect to your income, expenses, and asset holdings. The assumptions regarding investment returns, contract growth, cost of living increases, and/or inflation are hypothetical in nature, do not reflect actual investment results, and are not guarantees of future results and should be carefully considered. If any specific investment or contract is included in the plan, it must be accompanied by separate appropriate disclosures. This report is not complete without all pages.';

const SECTIONS: { title: string; body: string }[] = [
  {
    title: 'Assumptions',
    body: `This plan is intended to provide an analysis of your financial position and potential income in retirement. This plan incorporates the information provided by you, the client, with respect to your income, expenses and asset holdings. Income plans can offer one or more of the following characteristics: lifetime guarantees, flexibility, principal preservation and growth potential. Our goal is to help you build a plan that takes these needs into account, given your preferences, goals and objectives.

The plan recommendations are based on your current situation, your resources, and your goals. In addition, they are based on our current expectations of the behavior of the accounts and products being recommended. This is a hypothetical example only and is not intended to predict the actual performance of any specific product. The returns have been shown to continue unchanged for all years of the plan but this is not likely to occur and actual results may be more or less favorable. All investments have risks associated with them and future loss is possible.`,
  },
  {
    title: 'Criteria and Methodology',
    body: `The income plan may contain investment accounts, annuities, life policies, incomes like pensions and social security, income tax estimates, and detailed development of your retirement annual income target. The objective is to give you and your advisor the ability to show how these various pieces of a retirement puzzle can be brought together and structured to optimize income, minimize taxes and provide effective wealth transfer. The real power of the tool is creating scenarios which can be tested to see how these elements may be impacted under different conditions or using different planning concepts. The hypothetical variables include account growth, inflation, tax obligation, and the desired annual income target. Changing any of them will greatly impact the plan results.

For assets allocated to investment accounts, growth will be estimated using an average fixed rate which is hypothetical and not meant to indicate historical or future results. The plan may also show income distributions representing the amount of money to be withdrawn from the account. These income dollars may or may not be guaranteed and are subject to change. This illustrated income could represent a distribution of principal and/or interest depending on investment performance. The growth rates illustrated on this proposed income plan are for illustrative purposes only and are not guaranteed. These rates will change on a daily basis and also could be negative. Past performance is not an indication of future results.

For assets allocated to insurance contracts, the contract and any guarantees therein are subject to the claims paying ability of the carrier. Annuity projected growth rates may show income benefit base growth and not the market value of the annuity. Annuity distributions may be subject to withdrawal charges, premium bonus recapture charges and market value adjustments (if applicable) and may result in a loss of principal. Insurance company product recommendations must be accompanied by approved illustrations and/or brochures. Other investment recommendations must be accompanied by an approved prospectus.

If there are any insurance products or annuities within the plan presentation, the National Association of Insurance Commissioners has specifically required that the consumer be given an illustration disclosing all aspects of how that product works and what the minimum guarantees are. This plan does not generate the required illustration and that must be furnished separately. All income projections are hypothetical and should not be considered indicative of actual income. The income portion of this analysis does not take into account any taxes unless otherwise noted.`,
  },
  {
    title: 'Limitations and Risks',
    body: `The information contained in this report is not guaranteed to be accurate, complete or timely. Neither your advisor nor anyone who helped your advisor create or populate this report, including, but not limited to, any software or information provider, shall be liable for any damages or losses related to your use of the information contained in it. The information contained in the plan is to be used for informational purposes only.

The income plan does not provide tax advice. The tax calculations and tax projections shown in this plan are approximate and not intended to be accurate. An appropriate tax professional should be consulted prior to implementation of any strategy. The information provided in the plan is not intended to be used, nor can it be used for the purpose of avoiding U.S. Federal, state, or local tax penalties.

Potential Social Security Benefits shown in the plan are for informational purposes only. Potential Cost of Living increases are shown at a fixed rate. This is not likely to happen. Actual Social Security Benefits may be impacted by a number of different factors related to your personal situation. You should refer to the Social Security Administration for information on your future benefit. We are not affiliated with the Social Security Administration or any other government agency.`,
  },
];

interface Props {
  vm: DisclosuresVM;
}

/** Act 8 — Disclosures: required compliance text + plan identification. Static. */
export function ActDisclosures({ vm }: Props) {
  return (
    <Stage>
      <Eyebrow>Required disclosures</Eyebrow>
      <H1>Disclosures</H1>
      <Sub>
        This disclosure page must be included with all presentations made to the client. This report is not complete
        without all pages.
      </Sub>

      {/* Important notice — the bolded required statement, given top billing.
          deck-card keeps this short box whole across PDF page breaks. */}
      <div
        className="deck-card"
        style={{
          background: T.tealSoft,
          border: `1px solid ${T.teal}`,
          borderRadius: 12,
          padding: '18px 22px',
          marginBottom: 24,
        }}
      >
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: 1.2,
            textTransform: 'uppercase',
            color: T.tealDeep,
            marginBottom: 8,
          }}
        >
          Important
        </div>
        <p style={{ fontSize: 13.5, color: T.ink, lineHeight: 1.65, margin: 0 }}>{IMPORTANT_NOTICE}</p>
      </div>

      {/* The three legal sections. Deliberately NOT deck-card: these blocks are
          long enough to approach a full PDF page, and must be allowed to flow
          across page breaks instead of bumping wholesale. */}
      {SECTIONS.map((s) => {
        const paras = s.body.split('\n\n');
        return (
          <div
            key={s.title}
            style={{
              background: T.card,
              border: `1px solid ${T.grayLine}`,
              borderRadius: 12,
              padding: '20px 24px',
              marginBottom: 14,
            }}
          >
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: 1.2,
                textTransform: 'uppercase',
                color: T.gray,
                marginBottom: 10,
              }}
            >
              {s.title}
            </div>
            {paras.map((para, i) => (
              <p
                key={i}
                style={{
                  fontSize: 12.5,
                  color: T.ink,
                  lineHeight: 1.7,
                  margin: 0,
                  marginBottom: i < paras.length - 1 ? 10 : 0,
                }}
              >
                {para}
              </p>
            ))}
          </div>
        );
      })}

      {/* plan identification footer */}
      {(vm.planLine || vm.metaLine) && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 16,
            flexWrap: 'wrap',
            marginTop: 20,
            paddingTop: 14,
            borderTop: `1px solid ${T.grayLine}`,
            fontSize: 11.5,
            color: T.gray,
          }}
        >
          <span>{vm.planLine}</span>
          <span>{vm.metaLine}</span>
        </div>
      )}
    </Stage>
  );
}
